import {Inject, Injectable, OnDestroy} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { PatientWithNavigationProperties } from '@models/data/patient/Patient';
import {RegionService} from "@app/areas/auth/services/region.service";
import {APP_CONFIG, AppConfig} from "@src/app.config";
import {RequestService} from "@services/http/request/request.service";
import {take} from "rxjs/operators";
import { hasValue } from '@src/app/shared/utils/helpers';

@Injectable({
  providedIn: 'root',
})
export class ViewPatientService {
  private readonly patientsBase = `${this.appConfig.angularEndpoint}/patients`;
  private latestRequestedPatientId: string | null = null;
  public patient: Observable<PatientWithNavigationProperties>;
  private patientSubject: BehaviorSubject<PatientWithNavigationProperties>;

  public loading: Observable<boolean>;
  private loadingSubject: BehaviorSubject<boolean>;

  public error: Observable<boolean>;
  private errorSubject: BehaviorSubject<boolean>;

  constructor(
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private reqService: RequestService,
    private regionScv: RegionService
  ) {
    this.patientSubject = new BehaviorSubject<PatientWithNavigationProperties>(null);
    this.patient = this.patientSubject.asObservable();

    this.loadingSubject = new BehaviorSubject<boolean>(false);
    this.loading = this.loadingSubject.asObservable();

    this.errorSubject = new BehaviorSubject<boolean>(false);
    this.error = this.errorSubject.asObservable();
  }

  public clear(): void {
    this.latestRequestedPatientId = null;
    this.patientValue = null;
    this.loadingValue = false;
    this.errorValue = false;
  }

  public getDetailFromFmpId(id: string, isRetry = false): void {
    this.latestRequestedPatientId = id;
    this.loadingValue = true;
    this.errorValue = false;
    const url = `${this.patientsBase}/detail/${id}`

    this.reqService
      .get(url)
      .pipe(take(1))
      .subscribe({
        next: res => {
          if (this.latestRequestedPatientId !== id) {
            return;
          }

          const p = res.body as PatientWithNavigationProperties;

          // retry functionality if the users region did not match the currently selected one
          const regionsMatch = p.identityProfile.region === this.regionScv.selectedRegion$();
          this.patientValue = p;
          if (!isRetry && !regionsMatch) {
            this.getDetailFromFmpId(id, true)
          } else {
            this.loadingValue = false;
            this.errorValue = false;
          }
        },
        error: err => {
          if (this.latestRequestedPatientId !== id) {
            return;
          }

          console.error('err', err)
          this.patientValue = null;
          this.loadingValue = false;
          this.errorValue = true;
        }
      })
  }

  private set loadingValue(content: boolean) {
    this.loadingSubject.next(content);
  }

  private set errorValue(content: boolean) {
    this.errorSubject.next(content);
  }

  public set patientValue(content: PatientWithNavigationProperties) {
    this.patientSubject.next(content);
    if (hasValue(content)) {
      this.regionScv.selectRegion(content.identityProfile.region)
    }
  }

  public get patientValue(): PatientWithNavigationProperties {
    return this.patientSubject.getValue();
  }
}
